import React, { useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import GlobalDriftDialog from './GlobalDriftDialog';

import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import GroupIcon from '@mui/icons-material/GroupsOutlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcardOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import QuizIcon from '@mui/icons-material/QuizOutlined';
import CollectionsIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';

import { adminTaskApi } from '../../../api/adminTaskApi';
import { adminCohortApi } from '../../../api/adminCohortApi';
import { adminSweepstakesApi } from '../../../api/adminSweepstakesApi';
import { adminToSApi } from '../../../api/adminToSApi';
import { adminQuestionnaireApi } from '../../../api/adminQuestionnaireApi';
import { adminCollectionApi } from '../../../api/adminCollectionApi';
import { adminPlanCohortPurseMappingApi } from '../../../api/adminPlanCohortPurseMappingApi';
import { paths } from '../../../routes/paths';
import { colors, typo } from '../../../theme/tokens';

/* ------------------------------------------------------------------ */
/*  Global-entity registry                                             */
/* ------------------------------------------------------------------ */

type CountFn = () => Promise<number>;

interface GlobalEntry {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ sx?: any }>;
  path: string;
  /** Returns row count; loaded lazily per card. */
  countFn: CountFn;
}

const safeLength = <T,>(rowsP: Promise<T[]>): Promise<number> =>
  rowsP.then((rows) => rows.length).catch(() => 0);

const GLOBALS: GlobalEntry[] = [
  {
    key: 'tasks',
    label: 'Tasks',
    description: 'Member-facing tasks. Back-reference for cohort rewards and adventures.',
    icon: AssignmentIcon,
    path: paths.admin.globalCatalog.tasks(),
    countFn: () => safeLength(adminTaskApi.list()),
  },
  // Adventures removed from globals — they are tenant-scoped now.
  // See pages/Actions/AdventuresList (inline create) and migration 146.
  {
    key: 'cohorts',
    label: 'Cohorts',
    description: 'Segment definitions (rule JSON) that gate rewards and adventures.',
    icon: GroupIcon,
    path: paths.admin.globalCatalog.cohorts(),
    countFn: () => safeLength(adminCohortApi.list()),
  },
  {
    key: 'sweepstakes',
    label: 'Sweepstakes',
    description: 'Entry-based prize draws with start/end window + prize pool.',
    icon: CardGiftcardIcon,
    path: paths.admin.globalCatalog.sweepstakes(),
    countFn: () => safeLength(adminSweepstakesApi.list()),
  },
  {
    key: 'terms-of-service',
    label: 'Terms of Service',
    description: 'Versioned legal copy shown at onboarding / major consent events.',
    icon: DescriptionIcon,
    path: paths.admin.globalCatalog.termsOfService(),
    countFn: () => safeLength(adminToSApi.list()),
  },
  {
    key: 'questionnaires',
    label: 'Questionnaires',
    description: 'Surveys and assessments attached to tasks as gated completion criteria.',
    icon: QuizIcon,
    path: paths.admin.globalCatalog.questionnaires(),
    countFn: () => safeLength(adminQuestionnaireApi.list()),
  },
  {
    key: 'collections',
    label: 'Task reward collections',
    description: 'Named bundles that group task rewards for cohort assignment.',
    icon: CollectionsIcon,
    path: paths.admin.globalCatalog.collections(),
    countFn: () => safeLength(adminCollectionApi.list()),
  },
  {
    key: 'plan-cohort-purse',
    label: 'Plan · Cohort · Purse mappings',
    description: 'FIS funding routing: plan → cohort → purse composite mapping.',
    icon: AccountTreeIcon,
    path: paths.admin.globalCatalog.planCohortPurseMappings(),
    countFn: () => safeLength(adminPlanCohortPurseMappingApi.list()),
  },
];

/* ------------------------------------------------------------------ */
/*  Row                                                                */
/* ------------------------------------------------------------------ */

const CountPill: React.FC<{ count: number | null; empty: boolean }> = ({ count, empty }) => {
  if (count == null) return null;
  return (
    <Box
      sx={{
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'Manrope, sans-serif',
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        color: empty ? colors.warningText : colors.adminAccent,
        backgroundColor: empty ? colors.warningBgStrong : colors.adminAccentSubtle,
        borderRadius: '4px',
        px: 1,
        py: 0.25,
        flexShrink: 0,
      }}
    >
      {count} {count === 1 ? 'row' : 'rows'}
    </Box>
  );
};

const GlobalRowItem: React.FC<{ entry: GlobalEntry; isLast: boolean }> = ({ entry, isLast }) => {
  const navigate = useNavigate();
  const countQuery = useQuery({
    queryKey: ['admin', 'globals', entry.key, 'count'],
    queryFn: entry.countFn,
    staleTime: 30_000,
  });

  const count = countQuery.data;
  const isEmpty = count === 0;
  const Icon = entry.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.75,
        borderBottom: isLast ? 'none' : `1px solid ${colors.borderLight}`,
        cursor: 'pointer',
        ...(isEmpty ? { borderLeft: `2px dashed ${colors.warningBorder}` } : {}),
        '&:hover': { backgroundColor: colors.bgSubtle },
      }}
      onClick={() => navigate(entry.path)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') navigate(entry.path);
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          backgroundColor: colors.adminAccentSubtle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: colors.adminAccent, fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 600,
            fontSize: 14,
            color: colors.textPrimary,
          }}
        >
          {entry.label}
        </Typography>
        <Typography sx={{ ...typo.secondaryText, fontSize: 13 }}>{entry.description}</Typography>
      </Box>
      {countQuery.isLoading ? (
        <CircularProgress size={14} sx={{ color: colors.adminAccent }} />
      ) : (
        <CountPill count={count ?? 0} empty={isEmpty} />
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const GlobalCatalogLanding: React.FC = () => {
  const [driftOpen, setDriftOpen] = useState(false);
  return (
    <Box sx={{ maxWidth: 1120 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: colors.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              mb: 0.5,
            }}
          >
            Admin
          </Typography>
          <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Global Catalog</Typography>
          <Typography sx={{ ...typo.pageSubtitle }}>
            Platform entities shared across tenants. Pick one to list, edit, and publish.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<CompareArrowsIcon sx={{ fontSize: 18 }} />}
          onClick={() => setDriftOpen(true)}
          sx={{ textTransform: 'none', flexShrink: 0, borderColor: colors.borderDefault, color: colors.textPrimary, fontWeight: 600 }}
        >
          Check Global Drift
        </Button>
      </Box>

      <GlobalDriftDialog open={driftOpen} onClose={() => setDriftOpen(false)} />

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
          overflow: 'hidden',
        }}
      >
        {GLOBALS.map((entry, idx) => (
          <GlobalRowItem key={entry.key} entry={entry} isLast={idx === GLOBALS.length - 1} />
        ))}
      </Paper>
    </Box>
  );
};

export default GlobalCatalogLanding;
